"use client"

import { useState, useEffect } from "react"
import { auth, db } from "../Firebase/firebase-config"
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth"
import { ref, get } from "firebase/database"
import { useNavigate } from "react-router-dom"
import { useAccount } from "../context/AccountContext"
import "./PartnerLogin.css"

export default function PartnerLogin() {
  const { switchAccount } = useAccount()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [resetMsg, setResetMsg] = useState("")
  const navigate = useNavigate()

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const profileRef = ref(db, "profileDashboard")
          const snapshot = await get(profileRef)
          let mainUserId = null

          snapshot.forEach((mainSnap) => {
            const partners = mainSnap.child("partners").val()
            if (partners && partners[user.uid]) {
              mainUserId = mainSnap.key
            }
          })

          if (mainUserId) {
            // Switch to main account in AccountContext
            switchAccount(mainUserId)
            navigate("/partner-dashboard", { replace: true })
          }
        } catch (err) {
          console.error("Error checking partner on auth state change:", err)
        }
      }
    })

    return () => unsubscribe()
  }, [navigate, switchAccount])

  const handleLogin = async (e) => {
    e?.preventDefault()
    setError("")
    setResetMsg("")
    setLoading(true)

    try {
      // 1️⃣ Sign in
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const partnerUid = userCredential.user.uid

      // 2️⃣ Check if this UID exists under any main user's partners
      const profileRef = ref(db, "profileDashboard")
      const snapshot = await get(profileRef)
      let mainUserId = null

      snapshot.forEach((mainSnap) => {
        const partners = mainSnap.child("partners").val()
        if (partners && partners[partnerUid]) {
          mainUserId = mainSnap.key
        }
      })

      if (!mainUserId) {
        setError("This account is not registered as a partner.")
        await auth.signOut()
        setLoading(false)
        return
      }

      // 3️⃣ Switch Account via AccountContext
      switchAccount(mainUserId)

      navigate("/partner-dashboard")

    } catch (err) {
      console.error("Partner login error:", err)
      setError(err?.message || "Login failed")
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async (e) => {
    e?.preventDefault()
    setResetMsg("")
    setError("")
    try {
      if (!email) {
        setError("Please enter your email to reset the password.")
        return
      }
      await sendPasswordResetEmail(auth, email)
      setResetMsg("Password reset email sent. Please check your inbox.")
      setResetOpen(false)
    } catch (err) {
      setError(err?.message || "Failed to send reset email")
    }
  }

  return (
    <main style={{ minHeight: "100dvh", background: "#f5f7fa" }}>
      <section className="partner-login-container">
        <div className="partner-login-card">
          <div className="partner-login-card-header">
            <h1 className="partner-login-title">Welcome back</h1>
            <p className="login-subtle">Sign in to access your partner dashboard.</p>
          </div>
          <div className="partner-login-card-body">
            <form onSubmit={handleLogin} className="partner-login-form" noValidate>
              <div className="partner-form-field">
                <label htmlFor="email" className="partner-form-label">Email</label>
                <input
                  id="email"
                  className="partner-form-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="partner-form-field">
                <label htmlFor="password" className="partner-form-label">Password</label>
                <div className="password-wrapper">
                  <input
                    id="password"
                    className="partner-form-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              {error && <p className="partner-error-msg">{error}</p>}
              {resetMsg && <p className="partner-success-msg">{resetMsg}</p>}
              <div className="partner-form-actions">
                <button type="submit" className="partner-btn-primary" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In"}
                </button>
                <button type="button" className="partner-btn-link" onClick={() => setResetOpen(true)}>
                  Forgot password?
                </button>
              </div>
            </form>
          </div>
          <div className="partner-login-card-footer">
            <a href="/login" className="partner-inline-link">Back to login</a>
          </div>
        </div>
      </section>

      {resetOpen && (
        <div className="dialog-backdrop" role="dialog" onClick={(e) => e.target === e.currentTarget && setResetOpen(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="partner-login-title">Reset password</h2>
              <p className="login-subtle">Enter your email to receive a reset link.</p>
            </div>
            <form onSubmit={handleReset}>
              <div className="dialog-body partner-login-form">
                <div className="partner-form-field">
                  <label htmlFor="reset-email" className="partner-form-label">Email</label>
                  <input
                    id="reset-email"
                    className="partner-form-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="dialog-actions">
                <button type="button" className="btn-ghost" onClick={() => setResetOpen(false)}>Cancel</button>
                <button type="submit" className="partner-btn-primary">Send reset link</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  )
}
